import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getDeviceAsync,
  getDeviceGeneralAsync,
  getDeviceStatusAsync,
  getDeviceTelecareStatusHistoryAsync,
  getDeviceStatusHistoryListAsync,
  getDeviceStatusHistoryDetailAsync,
  searchDeviceEventsAsync,
  postNewDeviceAsync,
  getDeviceNotesAsync,
  getHistoricDeviceUsersAsync, searchDevicePeripheralEventsAsync
} from "./asyncActions";
import { initialState } from './initialState';
import { normalizeDataDeviceAction, normalizeGeneralData, normalizeStatusData } from './operations';
import { updateNotes } from '../userDetail/operations';

export const deviceDetailSlices = createSlice({
  name: 'deviceDetail',
  initialState,
  reducers: {
    resetDevice: (state) => {
      state.deviceData.data = null;
      state.generalData.data = null;
      state.statusData.data = null;
      state.statusHistory.items = [];
      state.statusHistory.total = 0;
      state.statusHistory.page = 0;
      state.statusHistory.fromDate = null;
      state.statusHistory.toDate = null;
      state.statusHistory.status = null;
      state.selectedHistoric.historicId = null;
      state.selectedHistoric.data = null;
      state.selectedHistoric.status = null;
      state.newDevice = null;
    },
    setStatusHistoryDateRange: (state, { payload }: PayloadAction<{ fromDate: number | null; toDate: number | null }>) => {
      state.statusHistory.fromDate = payload.fromDate;
      state.statusHistory.toDate = payload.toDate;
    },
    clearSelectedHistoric: (state) => {
      state.selectedHistoric.historicId = null;
      state.selectedHistoric.data = null;
      state.selectedHistoric.status = null;
    },
    resetNewDevice: (state) => {
      state.newDevice = null;
    },
    updateDeviceNotes: (state, { payload }: PayloadAction<any>) => {
      state.notes.data = updateNotes(payload);
    },
    setCommandSentErrorCode: (state, { payload }) => {
      state.commandSentErrorCode = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeviceAsync.pending, (state) => {
        state.deviceData.status = 'loading';
        state.deviceData.data = null;      
      })
      .addCase(getDeviceAsync.fulfilled, (state, action) => {
        state.deviceData.status = 'idle'
        state.deviceData.data = action.payload?.data?.item;      
      })
      .addCase(getDeviceAsync.rejected, (state) => {
        state.deviceData.status = 'failed'        
      })
      .addCase(postNewDeviceAsync.fulfilled, (state, action) => {
        state.newDevice =  action.payload?.data?.success;
      })
      .addCase(getDeviceGeneralAsync.pending, (state) => {
        state.generalData.status = 'loading'        
      })
      .addCase(getDeviceGeneralAsync.fulfilled, (state, action) => {
        state.generalData.status = 'idle'
        state.generalData.data = normalizeGeneralData(action.payload?.response?.data?.item, action.payload?.serviceStateLookup ?? {});
      })
      .addCase(getDeviceGeneralAsync.rejected, (state) => {
        state.generalData.status = 'failed'        
      })
      .addCase(getHistoricDeviceUsersAsync.pending, (state) => {
        state.historicUsers.status = 'loading';
      })
      .addCase(getHistoricDeviceUsersAsync.fulfilled, (state, action) => {
        state.historicUsers.status = 'idle';
        state.historicUsers.data = action.payload?.data?.item?.items;
        state.historicUsers.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getHistoricDeviceUsersAsync.rejected, (state) => {
        state.historicUsers.status = 'failed';
      })
      .addCase(getDeviceNotesAsync.pending, (state) => {
        state.notes.status = 'loading';
      })
      .addCase(getDeviceNotesAsync.fulfilled, (state, action) => {
        state.notes.status = 'idle';
        state.notes.data = action.payload?.data?.item?.items;
        state.notes.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getDeviceNotesAsync.rejected, (state) => {
        state.notes.status = 'failed';
      })
      .addCase(getDeviceStatusAsync.pending, (state) => {
        state.statusData.status = 'loading';
        state.statusData.data = null;     
      })
      .addCase(getDeviceStatusAsync.fulfilled, (state, action) => { 
        state.statusData.status = 'idle'
        state.statusData.data = normalizeStatusData(action.payload?.data);
      })
      .addCase(getDeviceStatusAsync.rejected, (state) => {
        state.statusData.status = 'failed'        
      })
      .addCase(getDeviceTelecareStatusHistoryAsync.pending, (state) => {
        state.statusGraphsData.status = 'loading';
        state.statusGraphsData.data = null;
        state.statusGraphsData.total = 0;   
      })
      .addCase(getDeviceTelecareStatusHistoryAsync.fulfilled, (state, action) => {
        state.statusGraphsData.status = 'idle'
        state.statusGraphsData.data = action.payload.data?.item?.items;
        state.statusGraphsData.total = action.payload.data?.item?.total;    
      })
      .addCase(getDeviceTelecareStatusHistoryAsync.rejected, (state) => {
        state.statusGraphsData.status = 'failed'
      })
      .addCase(getDeviceStatusHistoryListAsync.pending, (state) => {
        state.statusHistory.status = 'loading';
      })
      .addCase(getDeviceStatusHistoryListAsync.fulfilled, (state, { payload }) => {
        state.statusHistory.status = 'idle';
        const newItems = payload?.response?.data?.item?.items ?? [];
        state.statusHistory.items = payload?.append
          ? [...state.statusHistory.items, ...newItems]
          : newItems;
        state.statusHistory.total = payload?.response?.data?.item?.total ?? 0;
        state.statusHistory.page = payload?.page ?? 0;
      })
      .addCase(getDeviceStatusHistoryListAsync.rejected, (state) => {
        state.statusHistory.status = 'failed';
      })
      .addCase(getDeviceStatusHistoryDetailAsync.pending, (state, { meta }) => {
        state.selectedHistoric.status = 'loading';
        state.selectedHistoric.historicId = meta.arg.historicId;
      })
      .addCase(getDeviceStatusHistoryDetailAsync.fulfilled, (state, { payload }) => {
        state.selectedHistoric.status = 'idle';
        state.selectedHistoric.data = normalizeStatusData(payload?.response?.data);
        state.selectedHistoric.historicId = payload?.historicId;
      })
      .addCase(getDeviceStatusHistoryDetailAsync.rejected, (state) => {
        state.selectedHistoric.status = 'failed';
      })
      .addCase(searchDeviceEventsAsync.pending, (state) => {
        state.deviceActionData.status = 'loading'
      })
      .addCase(searchDeviceEventsAsync.fulfilled, (state, action) => {
        state.deviceActionData.status = 'idle'
        state.deviceActionData.data = normalizeDataDeviceAction(action.payload);
        // total + facetas sólo vienen en la primera página (init==0); en paginación se conservan.
        if (!action.meta?.arg?.init) {
          state.deviceActionData.filterableValues = action.payload?.data?.item?.filterableValues;
          state.deviceActionData.pagination.totalDevice = action.payload ? action.payload.data?.item?.total : 0 ;
        }
      })
      .addCase(searchDeviceEventsAsync.rejected, (state) => {
        state.deviceActionData.status = 'failed'
      })

      .addCase(searchDevicePeripheralEventsAsync.pending, (state) => {
        state.devicePeripheralActionData.status = 'loading'
      })
      .addCase(searchDevicePeripheralEventsAsync.fulfilled, (state, action) => {
        state.devicePeripheralActionData.status = 'idle'
        state.devicePeripheralActionData.data = normalizeDataDeviceAction(action.payload);
        // total + facetas sólo vienen en la primera página (init==0); en paginación se conservan.
        if (!action.meta?.arg?.init) {
          state.devicePeripheralActionData.filterableValues = action.payload?.data?.item?.filterableValues;
          state.devicePeripheralActionData.pagination.totalDevicePeripheral = action.payload ? action.payload.data?.item?.total : 0 ;
        }
      })
      .addCase(searchDevicePeripheralEventsAsync.rejected, (state) => {
        state.devicePeripheralActionData.status = 'failed'
      })
  }
});

export const {
  resetDevice,
  resetNewDevice,
  updateDeviceNotes,
  setCommandSentErrorCode,
  setStatusHistoryDateRange,
  clearSelectedHistoric,
} = deviceDetailSlices.actions;

export default deviceDetailSlices.reducer;
